import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Heart,
  ChatCircle,
  PaperPlaneTilt,
  BookmarkSimple,
  DotsThree,
  SealCheck,
  Play,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  } catch {
    return "";
  }
}

export default function PostCard({ post }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!post.liked);
  const [likes, setLikes] = useState(post.likes || 0);
  const [busy, setBusy] = useState(false);

  const author = post.author || {};
  const media = post.media_url;
  const kind = post.kind || "image";
  const isVideo = kind === "video" || kind === "live";

  const toggleLike = async () => {
    if (!user) {
      window.location.assign("/ingresar");
      return;
    }
    if (busy) return;
    setBusy(true);
    const next = !liked;
    setLiked(next);
    setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
    try {
      if (next) await api.post(`/posts/${post.id}/like`);
      else await api.delete(`/posts/${post.id}/like`);
    } catch {
      // revert
      setLiked(!next);
      setLikes((n) => Math.max(0, n + (next ? -1 : 1)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`post-${post.id}`}
      className="border-b border-[#27272A] bg-[#050505] fade-up"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-4 md:px-6">
        <Link
          to={`/artista/${author.handle || "unknown"}`}
          className="flex items-center gap-3 min-w-0"
        >
          <div className="h-11 w-11 overflow-hidden border border-[#27272A] shrink-0 bg-[#121212]">
            {author.photo ? (
              <img
                src={author.photo}
                alt={author.name}
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-display font-bold text-[15px] tracking-tight truncate">
                {author.name || author.handle}
              </span>
              {author.verified ? (
                <SealCheck size={14} weight="fill" className="text-[#FF3E00] shrink-0" />
              ) : null}
            </div>
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
              @{author.handle}
              {author.city ? ` · ${author.city.toUpperCase()}` : ""}
              {post.created_at ? ` · ${relativeTime(post.created_at)}` : ""}
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {kind === "live" ? (
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] bg-[#FF0033] text-white px-2 py-1">
              <span className="h-1.5 w-1.5 bg-white live-dot" />
              LIVE
            </span>
          ) : null}
          <button
            aria-label="Más"
            className="h-9 w-9 inline-flex items-center justify-center hover:bg-[#121212] text-[#A1A1AA] hover:text-white"
          >
            <DotsThree size={20} weight="bold" />
          </button>
        </div>
      </header>

      {/* Media */}
      {media ? (
        <Link to={`/post/${post.id}`} className="block relative group">
          <div className="relative aspect-[4/5] bg-[#121212] overflow-hidden">
            <img src={media} alt="post" className="w-full h-full object-cover" />
            {isVideo ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-16 w-16 bg-[#FF3E00] text-white flex items-center justify-center">
                  <Play size={24} weight="fill" />
                </div>
              </div>
            ) : null}
            <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur-sm border border-[#27272A] px-2 py-1 text-[#A1A1AA]">
              {kind.toUpperCase()}
            </div>
          </div>
        </Link>
      ) : (
        <div className="px-4 py-10 md:px-8 md:py-14 bg-[#0a0a0a] border-t border-b border-[#27272A]">
          <p className="font-display text-2xl md:text-3xl font-medium leading-[1.3] max-w-3xl">
            {post.caption}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between px-4 md:px-6 pt-3">
        <div className="flex items-center gap-1">
          <IconBtn
            onClick={toggleLike}
            testid={`like-${post.id}`}
            active={liked}
            icon={Heart}
            label={likes}
          />
          <Link
            to={`/post/${post.id}`}
            data-testid={`comments-${post.id}`}
            className="flex items-center gap-1.5 px-2 py-2 text-[#A1A1AA] hover:text-white font-mono text-xs"
          >
            <ChatCircle size={22} weight="regular" />
            <span>{post.comments || 0}</span>
          </Link>
          <IconBtn icon={PaperPlaneTilt} label={post.shares || 0} />
        </div>
        <IconBtn icon={BookmarkSimple} />
      </div>

      {media && post.caption ? (
        <div className="px-4 md:px-6 py-3">
          <p className="text-[15px] leading-[1.55]">
            <Link
              to={`/artista/${author.handle}`}
              className="font-display font-bold mr-2"
            >
              {author.name}
            </Link>
            <span className="text-[#E5E5E5]">{post.caption}</span>
          </p>
        </div>
      ) : null}

      {(post.tags || []).length > 0 ? (
        <div className="flex flex-wrap gap-2 px-4 md:px-6 pb-5">
          {post.tags.map((t) => (
            <span
              key={t}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#FF3E00] hover:text-white cursor-pointer"
            >
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function IconBtn({ icon: Icon, label, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-2 py-2 font-mono text-xs transition-colors ${
        active ? "text-[#FF3E00]" : "text-[#A1A1AA] hover:text-white"
      }`}
    >
      <Icon size={22} weight={active ? "fill" : "regular"} />
      {label !== undefined ? (
        <span>{typeof label === "number" ? label.toLocaleString("es") : label}</span>
      ) : null}
    </button>
  );
}
